<template>
  <div class="zip-range-container">
    <el-input
      v-model="displayValue"
      readonly
      :placeholder="$t('CenterForSorting.pleaseInput')"
      size="small"
      @click.native="openDialog"
    >
      <i slot="suffix" class="el-input__icon el-icon-edit-outline pointer" @click.stop="openDialog" />
    </el-input>

    <el-dialog
      :title="$t('CenterForSorting.zipRangeConfig')"
      :visible.sync="dialogVisible"
      width="600px"
      append-to-body
      :close-on-click-modal="false"
      custom-class="zip-range-dialog"
    >
      <div class="zip-range-editor">
        <div v-if="localList.length === 0" class="empty-tip">
          {{ $t('CenterForSorting.noData') }}
        </div>
        <div v-for="(item, index) in localList" :key="index" class="zip-item-row">
          <el-row :gutter="10" type="flex" align="middle">
            <el-col :span="10">
              <el-input
                v-model="item.start"
                :placeholder="$t('CenterForSorting.startZip')"
                maxlength="8"
                clearable
                :disabled="disabled"
                @input="validateItem(item)"
              />
            </el-col>
            <el-col :span="2" style="text-align: center;">~</el-col>
            <el-col :span="10">
              <el-input
                v-model="item.end"
                :placeholder="$t('CenterForSorting.endZip')"
                maxlength="8"
                clearable
                :disabled="disabled"
                @input="validateItem(item)"
              />
            </el-col>
            <el-col :span="2">
              <el-button
                type="danger"
                icon="el-icon-minus"
                circle
                size="mini"
                :disabled="disabled"
                @click="removeItem(index)"
              />
            </el-col>
          </el-row>
          <div v-if="item.error" class="error-msg">{{ item.error }}</div>
        </div>
        <div class="add-btn-wrapper">
          <el-button
            type="primary"
            plain
            icon="el-icon-plus"
            size="small"
            style="width: 100%;"
            :disabled="disabled"
            @click="addItem"
          >
            {{ $t('CenterForSorting.addZipRange') }}
          </el-button>
        </div>

        <!-- Visualization Section -->
        <div v-if="hasValidRanges" class="visualization-container">
          <div class="chart-header">
            <div class="chart-title">{{ $t('CenterForSorting.zipDistribution') }}</div>
            <div class="chart-note">{{ $t('CenterForSorting.zipSchematicHint') }}</div>
          </div>
          <div
            class="chart-wrapper"
          >
            <svg
              ref="chartSvg"
              :viewBox="`0 0 ${svgWidth} ${svgHeight}`"
              class="zip-chart"
              preserveAspectRatio="none"
            >
              <!-- Axis Line -->
              <line
                :x1="padding"
                :y1="axisY"
                :x2="svgWidth - padding"
                :y2="axisY"
                stroke="#E4E7ED"
                stroke-width="2"
              />

              <!-- Segments (Green for normal, Red for overlap) -->
              <g v-for="segment in processedSegments" :key="`seg-${segment.start}-${segment.end}`">
                <line
                  v-bind="getSegmentCoords(segment)"
                  :y1="axisY"
                  :y2="axisY"
                  :stroke="segment.isOverlap ? '#F56C6C' : '#67C23A'"
                  :stroke-width="segmentStrokeWidth"
                  stroke-linecap="round"
                  class="chart-segment"
                />
              </g>

              <!-- Points (Circles for start/end of user inputs) -->
              <g v-for="(point, idx) in processedPoints" :key="'pt-' + idx">
                <circle
                  :cx="getVisualX(point.value)"
                  :cy="axisY"
                  :r="pointRadius"
                  :fill="point.isOverlap ? '#F56C6C' : '#67C23A'"
                  stroke="#fff"
                  stroke-width="2"
                  class="chart-point"
                  @mouseenter="showTooltip($event, point.value)"
                  @mouseleave="hideTooltip"
                />
              </g>
            </svg>

            <!-- Custom Tooltip -->
            <transition name="el-fade-in-linear">
              <div
                v-show="tooltip.visible"
                class="chart-tooltip"
                :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }"
              >
                <div class="tooltip-content">
                  <span class="tooltip-label">{{ $t('CenterForSorting.zipCode') }}:</span>
                  <span class="tooltip-value">{{ tooltip.content }}</span>
                </div>
                <div class="tooltip-arrow" />
              </div>
            </transition>
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">{{ $t('CenterForSorting.cancel') }}</el-button>
        <el-button type="primary" :disabled="disabled" @click="handleConfirm">{{ $t('CenterForSorting.confirm') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'ZipRange',
  props: {
    value: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialogVisible: false,
      localList: [], // Array of { start: '', end: '', error: '' } 存储邮编范围的数组，每项包含 start, end, error
      displayValue: '',
      // 可视化配置
      svgWidth: 800,
      svgHeight: 100,
      padding: 30,
      axisY: 50,
      tooltip: {
        visible: false,
        x: 0,
        y: 0,
        content: ''
      }
    };
  },
  computed: {
    hasValidRanges() {
      return this.validRanges.length > 0;
    },
    // 示意图边界点：只保留区间端点并按从小到大排序
    visualBoundaries() {
      const points = new Set();
      this.validRanges.forEach(r => {
        points.add(r.start);
        points.add(r.end);
      });
      return Array.from(points).sort((a, b) => a - b);
    },
    // 端点到序号映射，示意图按序号等距渲染，不按真实邮编距离
    boundaryIndexMap() {
      const map = new Map();
      this.visualBoundaries.forEach((value, index) => {
        map.set(value, index);
      });
      return map;
    },
    // 原始有效范围
    validRanges() {
      return this.localList
        .filter(item => {
          const start = this.normalizeZipInput(item.start);
          // 只要起始邮编合法就参与渲染，结束值输入中可暂不参与
          return /^\d{8}$/.test(start);
        })
        .map(item => {
          const start = this.normalizeZipInput(item.start);
          const end = this.normalizeZipInput(item.end);
          const s = parseInt(start, 10);
          let e = s;
          // 结束邮编合法时参与计算，否则按单点处理
          if (end && /^\d{8}$/.test(end)) {
            e = parseInt(end, 10);
          }
          // 统一保证 start <= end，避免输入顺序导致线段丢失
          return {
            start: Math.min(s, e),
            end: Math.max(s, e)
          };
        });
    },
    // 线段较多时自动细化，避免图形拥挤
    segmentStrokeWidth() {
      const count = this.processedSegments.length;
      if (count >= 20) return 4;
      if (count >= 10) return 6;
      return 8;
    },
    // 点位较多时自动缩小圆点，避免互相遮挡
    pointRadius() {
      const count = this.processedPoints.length;
      if (count >= 40) return 3;
      if (count >= 20) return 4;
      return 6;
    },
    // 计算渲染的线段（绿色/红色）
    processedSegments() {
      if (!this.hasValidRanges) return [];

      const sortedPoints = this.visualBoundaries;
      if (sortedPoints.length === 0) return [];

      const segments = [];

      // 遍历由点定义的区间
      for (let i = 0; i < sortedPoints.length - 1; i++) {
        const p1 = sortedPoints[i];
        const p2 = sortedPoints[i + 1];

        // 中点检查
        // 检查线段 (p1, p2) 是否被任何范围覆盖
        const mid = (p1 + p2) / 2;

        let coverage = 0;
        this.validRanges.forEach(r => {
          if (r.start <= mid && r.end >= mid) {
            coverage++;
          }
        });

        if (coverage > 0) {
          segments.push({
            start: p1,
            end: p2,
            isOverlap: coverage > 1
          });
        }
      }

      return segments;
    },
    // 计算渲染的点（圆圈）
    processedPoints() {
      if (!this.hasValidRanges) return [];

      const pointMap = new Map(); // value -> count

      this.validRanges.forEach(r => {
        // 起始点
        pointMap.set(r.start, (pointMap.get(r.start) || 0) + 1);

        // 结束点（如果不同）
        if (r.start !== r.end) {
          pointMap.set(r.end, (pointMap.get(r.end) || 0) + 1);
        }
      });

      const result = [];
      pointMap.forEach((cnt, val) => {
        // 检查真实的覆盖计数
        let coverage = 0;
        this.validRanges.forEach(r => {
          if (r.start <= val && r.end >= val) {
            coverage++;
          }
        });

        result.push({
          value: val,
          isOverlap: coverage > 1
        });
      });

      return result.sort((a, b) => a.value - b.value);
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.displayValue = val;
      }
    }
  },
  methods: {
    // 统一清洗输入，兼容字符串与空值
    normalizeZipInput(value) {
      return String(value || '').trim();
    },
    // 按端点序号等距映射到 X 轴，保证所有线段和点都能完整展示
    getVisualX(val) {
      const count = this.visualBoundaries.length;
      if (count <= 1) return this.svgWidth / 2;

      const index = this.boundaryIndexMap.get(val);
      const safeIndex = typeof index === 'number' ? index : 0;
      const width = this.svgWidth - 2 * this.padding;
      const step = width / (count - 1);
      return this.padding + safeIndex * step;
    },
    // 获取线段坐标并设置最小显示宽度
    getSegmentCoords(segment) {
      let x1 = this.getVisualX(segment.start);
      let x2 = this.getVisualX(segment.end);

      // 线段在端点过密时仍保持可见
      const minPixelWidth = 4;
      if (Math.abs(x2 - x1) < minPixelWidth) {
        const center = (x1 + x2) / 2;
        x1 = center - minPixelWidth / 2;
        x2 = center + minPixelWidth / 2;
      }
      return { x1, x2 };
    },
    // 提示框显示与隐藏
    showTooltip(e, val) {
      this.tooltip.visible = true;
      this.tooltip.content = val;

      const rect = e.target.getBoundingClientRect();

      // 使用 fixed 定位，基于视口坐标，避免被 overflow: hidden 遮挡
      this.tooltip.x = rect.left + rect.width / 2;
      this.tooltip.y = rect.top;
    },
    hideTooltip() {
      this.tooltip.visible = false;
    },
    // 打开弹窗，解析当前值
    openDialog() {
      this.parseValue();
      this.dialogVisible = true;
    },
    // 解析字符串值为对象数组
    parseValue() {
      this.localList = [];
      if (!this.value) {
        this.addItem();
        return;
      }

      // 兼容英文逗号、中文逗号，并清理空白项
      const parts = String(this.value)
        .replace(/～/g, '~')
        .split(/[，,]/)
        .map(part => part.trim())
        .filter(Boolean);

      parts.forEach(part => {
        if (part.includes('~')) {
          const [startRaw, endRaw] = part.split('~');
          const start = this.normalizeZipInput(startRaw);
          const end = this.normalizeZipInput(endRaw);
          this.localList.push({ start, end: start === end ? '' : end, error: '' });
        } else {
          this.localList.push({ start: this.normalizeZipInput(part), end: '', error: '' });
        }
      });

      if (this.localList.length === 0) {
        this.addItem();
      }
    },
    // 新增一行
    addItem() {
      this.localList.push({ start: '', end: '', error: '' });
    },
    // 删除一行
    removeItem(index) {
      this.localList.splice(index, 1);
    },
    // 校验单行数据
    validateItem(item) {
      item.error = '';
      const zipRegex = /^\d{8}$/;

      // 允许输入过程中为空，确认时再校验必填
      if (item.start && !zipRegex.test(item.start)) {
        item.error = this.$t('CenterForSorting.zipFormatError'); // 邮编格式错误
        return false;
      }

      if (item.end && !zipRegex.test(item.end)) {
        item.error = this.$t('CenterForSorting.zipFormatError');
        return false;
      }

      if (item.start && item.end) {
        if (Number(item.start) > Number(item.end)) {
          item.error = this.$t('CenterForSorting.startGreaterThanEnd'); // 起始邮编不能大于结束邮编
          return false;
        }
      }

      return true;
    },
    // 确认保存
    handleConfirm() {
      // 过滤空行（如果start和end都为空）
      const validItems = this.localList.filter(item => item.start || item.end);

      if (validItems.length === 0) {
        this.$message.warning(this.$t('CenterForSorting.notNull'));
        return;
      }

      // 全局校验
      let isValid = true;
      for (const item of validItems) {
        if (!this.validateItem(item)) {
          isValid = false;
        }
        // 如果只填了结束没填开始
        if (!item.start && item.end) {
          item.error = this.$t('CenterForSorting.startZipRequired'); // 请输入起始邮编
          isValid = false;
        }
        // 如果是单值（只有start），确保是8位
        if (item.start && !item.end && !/^\d{8}$/.test(item.start)) {
          item.error = this.$t('CenterForSorting.zipFormatError');
          isValid = false;
        }
        // 如果是范围，确保都有值且格式正确
        if (item.start && item.end) {
          if (!/^\d{8}$/.test(item.start) || !/^\d{8}$/.test(item.end)) {
            item.error = this.$t('CenterForSorting.zipFormatError');
            isValid = false;
          }
        }
      }

      if (!isValid) return;

      // 检查重复
      const overlapError = this.checkOverlap(validItems);
      if (overlapError) {
        this.$message.error(overlapError);
        return;
      }

      const result = validItems.map(item => {
        if (item.end && item.start !== item.end) {
          return `${item.start}~${item.end}`;
        } else {
          return item.start;
        }
      }).join(',');

      this.$emit('input', result);
      this.displayValue = result;
      this.dialogVisible = false;
    },
    // 检查是否有重叠
    checkOverlap(items) {
      // 转换为数值区间 [start, end, displayStr]
      const ranges = items.map(item => {
        const start = parseInt(item.start, 10);
        const end = item.end ? parseInt(item.end, 10) : start;
        const display = (item.end && item.start !== item.end) ? `${item.start}~${item.end}` : `${item.start}`;
        return { start, end, display };
      });

      // 按开始值排序
      ranges.sort((a, b) => a.start - b.start);

      for (let i = 0; i < ranges.length - 1; i++) {
        const current = ranges[i];
        const next = ranges[i + 1];

        // 如果当前的结束值 >= 下一个的开始值，则重叠
        if (current.end >= next.start) {
          // 构造错误提示
          // 提示如：78889972与78888000~78889972重叠
          return this.$t('CenterForSorting.overlapError', { range1: current.display, range2: next.display });
        }
      }
      return null;
    }
  }
};
</script>

<style scoped>
.pointer {
  cursor: pointer;
}
.zip-item-row {
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px dashed #eee;
}
.zip-item-row:last-child {
  border-bottom: none;
}
.error-msg {
  color: #f56c6c;
  font-size: 12px;
  margin-top: 5px;
}
.add-btn-wrapper {
  margin-top: 10px;
}
.empty-tip {
  text-align: center;
  color: #999;
  padding: 20px;
}
/* Dialog customization if needed / 如果需要，自定义弹窗样式 */
.zip-range-editor {
  max-height: 400px;
  overflow-y: auto;
  padding-right: 5px;
}
.visualization-container {
  margin-top: 20px;
  border-top: 1px solid #eee;
  padding-top: 10px;
}
.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}
.chart-title {
  font-size: 14px;
  color: #606266;
  font-weight: bold;
}
.chart-note {
  font-size: 12px;
  color: #909399;
}
.chart-wrapper {
  position: relative;
  width: 100%;
  height: 120px;
  overflow: hidden;
  user-select: none;
}
.zip-chart {
  width: 100%;
  height: 100%;
  background-color: #fdfdfd;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  cursor: default;
}
.chart-point {
  cursor: pointer;
  transition: all 0.2s;
}
.chart-point:hover {
  r: 8;
  stroke: #303133;
  stroke-width: 3;
}
.chart-tooltip {
    position: fixed;
    background: #303133;
    color: #fff;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 12px;
    pointer-events: none;
    transform: translate(-50%, -100%);
    margin-top: -12px;
    z-index: 9999;
    white-space: nowrap;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}
.tooltip-content {
    display: flex;
    align-items: center;
    gap: 8px;
}
.tooltip-label {
    color: #B0B3B8;
}
.tooltip-value {
    font-weight: bold;
    color: #fff;
    font-family: monospace;
}
.tooltip-arrow {
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: #303133 transparent transparent transparent;
}
</style>
